import { useEffect, useId, useState } from 'react';
import { DateTime } from 'luxon';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export type DateFieldProps = {
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  id?: string;
  /** Whether the user may blank the field (it then reports null). Off by default: the legacy pickers could not be blanked. */
  clearable?: boolean;
};

const DISPLAY = 'MM/dd/yyyy';
const ACCEPTED = ['M/d/yyyy', 'M/d/yy', 'MMddyyyy', 'MMddyy'];
const TWO_DIGIT_YEAR = new Set(['M/d/yy', 'MMddyy']);

const format = (date: Date | null) => (date ? DateTime.fromJSDate(date).toFormat(DISPLAY) : '');

// The year is two digits or four (D7), and a four-digit year doesn't start with 0. luxon's patterns
// alone take a half-typed year: `yy` reads "202" and `yyyy` reads "0025".
const SHAPES = [/^\d{1,2}\/\d{1,2}\/(\d{2}|[1-9]\d{3})$/, /^\d{4}(\d{2}|[1-9]\d{3})$/];

/** Reads MM/DD/YY or MM/DD/YYYY (slashes optional) as a local calendar date. */
export const parseDateText = (text: string): Date | null | undefined => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (!SHAPES.some((shape) => shape.test(trimmed))) return undefined;
  for (const pattern of ACCEPTED) {
    const parsed = DateTime.fromFormat(trimmed, pattern);
    if (!parsed.isValid) continue;
    if (TWO_DIGIT_YEAR.has(pattern)) {
      // As the range filters read YY before (moment): 00-68 is 20xx, 69-99 is 19xx.
      const yy = parsed.year % 100;
      return parsed
        .set({ year: yy > 68 ? 1900 + yy : 2000 + yy })
        .startOf('day')
        .toJSDate();
    }
    return parsed.startOf('day').toJSDate();
  }
  return undefined;
};

// The same calendar day; a value's time of day is the caller's business.
const sameDay = (a: Date | null, b: Date | null) => format(a) === format(b);

/**
 * A labelled date input: type MM/DD/YY or MM/DD/YYYY, or pick from the calendar. It shows the
 * date as MM/DD/YYYY and reports a local-midnight Date (or null when cleared, if `clearable`). A caller whose value
 * carries a timezone or a time of day converts at its own edge.
 */
export function DateField({ label, value, onChange, disabled = false, minDate, maxDate, id: idProp, clearable = false }: DateFieldProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const shown = format(value);
  const [text, setText] = useState(shown);
  const [open, setOpen] = useState(false);

  // Callers build their Date each render, so follow the date shown, not the object.
  useEffect(() => {
    setText(shown);
  }, [shown]);

  const inRange = (date: Date) => (!minDate || date >= minDate) && (!maxDate || date <= maxDate);

  const commit = () => {
    const parsed = parseDateText(text);
    if (parsed === undefined || (parsed && !inRange(parsed)) || (parsed === null && !clearable)) {
      setText(format(value));
      return;
    }
    setText(format(parsed));
    if (!sameDay(parsed, value)) onChange(parsed);
  };

  const calendarProps = {
    ...(value ? { defaultMonth: value } : {}),
    disabled: (date: Date) => !inRange(date),
    onSelect: (date: Date | undefined) => {
      setOpen(false);
      if (!date && !clearable) return;
      const next = date ?? null;
      setText(format(next));
      if (!sameDay(next, value)) onChange(next);
    },
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-1">
        <Input
          id={id}
          value={text}
          placeholder="MM/DD/YYYY"
          disabled={disabled}
          onChange={(event) => setText(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit();
          }}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger render={<Button type="button" variant="outline" size="icon" disabled={disabled} aria-label={`Choose ${label} date`} />}>
            <CalendarIcon />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            {clearable ? (
              <Calendar mode="single" {...(value ? { selected: value } : {})} {...calendarProps} />
            ) : (
              <Calendar mode="single" required selected={value ?? undefined} {...calendarProps} />
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
